import { Link as MuiLink, LinkProps as MuiLinkProps, styled } from '@mui/material';
import React, { type JSX } from 'react';

import { linkable } from '../../routing/links/linkable';
import { body, colorSystem } from '../../theme';
import type { IconElement } from '../icons/IconElement';
import { iconSlot } from '../icons/iconSlot';
import { FontVariant, Text } from '../Text';

export type LinkProps = Omit<MuiLinkProps, 'variant'> & {
    attention?: 'high' | 'low' | 'mui-default';
    startIcon?: IconElement;
    endIcon?: IconElement;
    variant?: FontVariant;
    showEllipsis?: boolean;
    textStyle?: React.CSSProperties;
};

const WRAPPER_STYLE: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '4px' };

/** Undoes MUI's button-mode centring, middle alignment and `user-select: none`; a caller's `sx` still wins. */
const BUTTON_MODE_SX = { textAlign: 'inherit', verticalAlign: 'baseline', userSelect: 'text' } as const;

const LinkHost = React.forwardRef(
    (
        {
            attention = 'mui-default',
            children,
            startIcon,
            endIcon,
            variant,
            textStyle,
            showEllipsis,
            ...linkProps
        }: LinkProps,
        ref: React.Ref<HTMLAnchorElement>,
    ): JSX.Element => {
        // A bare <button> defaults to type "submit": keep it from submitting a form, and lay it out like an anchor.
        const props =
            linkProps.component === 'button'
                ? {
                      type: 'button',
                      ...linkProps,
                      sx: [BUTTON_MODE_SX, ...(Array.isArray(linkProps.sx) ? linkProps.sx : [linkProps.sx])],
                  }
                : linkProps;

        // Without the cap, the wrapper grows to the full text width, so the text never overflows to truncate.
        const wrapperStyle: React.CSSProperties = showEllipsis ? { ...WRAPPER_STYLE, maxWidth: '100%' } : WRAPPER_STYLE;
        switch (attention) {
            case 'high':
                return (
                    <HighAttentionLink ref={ref} {...props}>
                        <div style={wrapperStyle}>
                            {startIcon}
                            <HighAttentionText
                                variant={variant}
                                showEllipsis={showEllipsis}
                                color="inherit"
                                style={textStyle}
                            >
                                {children}
                            </HighAttentionText>
                            {endIcon}
                        </div>
                    </HighAttentionLink>
                );
            case 'low':
                return (
                    <LowAttentionLink ref={ref} {...props}>
                        <div style={wrapperStyle}>
                            {startIcon}
                            <Text
                                variant={variant}
                                color="inherit"
                                showEllipsis={showEllipsis}
                                style={{ textDecoration: 'underline', ...textStyle }}
                            >
                                {children}
                            </Text>
                            {endIcon}
                        </div>
                    </LowAttentionLink>
                );
            case 'mui-default':
                return (
                    <DefaultAttentionLink ref={ref} {...props} style={{ cursor: 'pointer', ...props.style }}>
                        <div style={wrapperStyle}>
                            {startIcon}
                            <Text variant={variant} showEllipsis={showEllipsis} color="inherit" style={textStyle}>
                                {children}
                            </Text>
                            {endIcon}
                        </div>
                    </DefaultAttentionLink>
                );
        }
    },
);

// Sizes the start and end icons, the wrapper's direct svgs: icons inside `children` keep their own size.
const SLOT_ICON = { '& > div': iconSlot(14) };

const HighAttentionLink = styled(MuiLink)({
    ...body,
    display: 'inline',
    cursor: 'pointer',
    color: colorSystem.blue[6],
    // The link's font on every svg in it, so an icon at `size="1em"` in the label takes the text size.
    '& * > svg': {
        ...body,
    },
    ...SLOT_ICON,
});

const LowAttentionLink = styled(MuiLink)({
    ...body,
    cursor: 'pointer',
    color: colorSystem.neutral[6],
    '&:hover': {
        color: colorSystem.blue[6],
    },
    '& * > svg': {
        ...body,
    },
    ...SLOT_ICON,
});

const DefaultAttentionLink = styled(MuiLink)(SLOT_ICON);

const HighAttentionText = styled(Text)({
    '&:hover': { textDecoration: 'underline' },
});

/** A text link: `to` for an app route, `href` for a URL, or `component="button"` and `onClick` for an action. */
export const Link = linkable(LinkHost);
